import React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import LayersIcon from "@mui/icons-material/Layers";
import FilterListIcon from "@mui/icons-material/FilterList";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import { SectionHeader } from "../layout/sectionHeader";
import { useTranslation } from "react-i18next";
import MuiMenu from "../framework/muiMenu";
import { MuiSwitch } from "../framework/muiSwitch";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import {
    setGroupMapPoints,
} from "../../store/monitorViewSlice";
import {
    setDrawingMode,
    setEditMode,
    setShowOnMap,
    setFilterCustomerIds,
    setFilterCostCenterIds,
} from "../../store/geofenceSlice";
import { GeofenceShapeTypeName } from "../../models/geofence/geofence";
import { useGeofenceFilter } from "../../hooks/monitorView/useGeofenceFilter";

interface MapToolbarProps {
    zoom: () => void;
    onRefresh: () => void;
    loading: boolean;
    groupPoints: boolean;
}

export const MapToolbar = React.memo(({
    zoom,
    onRefresh,
    loading,
    groupPoints,
}: MapToolbarProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const drawingMode = useAppSelector((state) => state.geofence.drawingMode);
    const editMode = useAppSelector((state) => state.geofence.editMode);
    const showOnMap = useAppSelector((state) => state.geofence.showOnMap);

    const {
        filterAnchorEl,
        setFilterAnchorEl,
        filterCustomers,
        filterCostCenters,
        filterCustomerIds,
        filterCostCenterIds,
        availableCustomers,
        availableCostCenters,
    } = useGeofenceFilter();

    return (
        <SectionHeader>
            <Typography
                component="div"
                sx={{ flexGrow: 1 }}
                className="section-header text-truncate"
            >
                {t("monitor")}
            </Typography>
            <IconButton
                color="inherit"
                onClick={() => zoom()}
                title={t("map.centerFocus")}
            >
                <CenterFocusStrongIcon />
            </IconButton>
            <IconButton color="inherit" onClick={onRefresh} disabled={loading}>
                <RefreshIcon />
            </IconButton>
            <IconButton
                color={showOnMap ? "primary" : "inherit"}
                onClick={() => dispatch(setShowOnMap(!showOnMap))}
                title={t("monitorView.geofence.showOnMap")}
            >
                <LayersIcon />
            </IconButton>
            {showOnMap && (
                <IconButton
                    color={
                        filterCustomerIds.length || filterCostCenterIds.length
                            ? "primary"
                            : "inherit"
                    }
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    title={t("monitorView.geofence.filter")}
                >
                    <FilterListIcon />
                </IconButton>
            )}
            {showOnMap && (
                <IconButton
                    color={editMode ? "primary" : "inherit"}
                    onClick={() => dispatch(setEditMode(!editMode))}
                    title={t("monitorView.geofence.editMode")}
                >
                    <EditLocationIcon />
                </IconButton>
            )}
            {showOnMap && editMode && (
                <ToggleButtonGroup
                    size="small"
                    value={drawingMode}
                    exclusive
                    onChange={(_, val) =>
                        dispatch(
                            setDrawingMode(val as GeofenceShapeTypeName | null)
                        )
                    }
                    sx={{
                        alignSelf: "center",
                        backgroundColor: "rgb(255,255,255)",
                        borderRadius: 1,
                    }}
                >
                    <ToggleButton
                        color="secondary"
                        value="Circle"
                        title={t("monitorView.geofence.circle")}
                    >
                        <CircleOutlinedIcon fontSize="small" color="primary" />
                    </ToggleButton>
                    <ToggleButton
                        value="Rectangle"
                        title={t("monitorView.geofence.rectangle")}
                    >
                        <CropSquareIcon fontSize="small" color="primary" />
                    </ToggleButton>
                    <ToggleButton
                        value="Polygon"
                        title={t("monitorView.geofence.polygon")}
                    >
                        <PentagonOutlinedIcon
                            fontSize="small"
                            color="primary"
                        />
                    </ToggleButton>
                </ToggleButtonGroup>
            )}
            <MuiMenu
                label={<SettingsIcon />}
                buttonColor="inherit"
                keepOpenAfterSelect
                items={[
                    {
                        label: (
                            <MuiSwitch
                                label={t("map.groupIcons")}
                                checked={groupPoints}
                                readOnly
                                onChange={(_, checked) =>
                                    dispatch(setGroupMapPoints(checked))
                                }
                            />
                        ),
                    },
                ]}
            />
            <Popover
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Box
                    sx={{
                        p: 1.5,
                        width: 260,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    <Autocomplete
                        multiple
                        size="small"
                        options={availableCustomers}
                        value={filterCustomers.filter((c) =>
                            filterCustomerIds.includes(c.id)
                        )}
                        onChange={(_, val) =>
                            dispatch(setFilterCustomerIds(val.map((v) => v.id)))
                        }
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("monitorView.geofence.customer")}
                            />
                        )}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        options={availableCostCenters}
                        value={filterCostCenters.filter((c) =>
                            filterCostCenterIds.includes(c.id)
                        )}
                        onChange={(_, val) =>
                            dispatch(
                                setFilterCostCenterIds(val.map((v) => v.id))
                            )
                        }
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("monitorView.geofence.costCenter")}
                            />
                        )}
                    />
                </Box>
            </Popover>
        </SectionHeader>
    );
});

MapToolbar.displayName = "MapToolbar";
